import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Card, Row, Col, Statistic, Tag, Button, Space, Input, Select,
  Modal, Form, message, Typography, Tooltip, Badge, Descriptions, Divider,
  Alert, Progress, List,
} from 'antd';
import {
  ShoppingOutlined, DollarOutlined, ClockCircleOutlined, GiftOutlined,
  SearchOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined,
  CrownOutlined, StarOutlined, ReloadOutlined, ThunderboltOutlined,
  CheckCircleFilled, CloseCircleFilled, PlayCircleOutlined,
} from '@ant-design/icons';
import { useLanguage } from '../store/LanguageContext';
import { orderAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface Order {
  id: number;
  tenant?: 'CN' | 'GLOBAL';
  currency?: 'CNY' | 'USD';
  order_no: string;
  feedback_id?: number;
  user_id?: number;
  plan_type: 'free' | 'standard' | 'premium';
  amount: number;
  payment_status: 'PENDING' | 'PAID' | 'EXPIRED' | 'REFUNDED';
  payment_method?: 'wechat' | 'alipay' | 'paypal' | 'other';
  tool_name: string;
  tool_url?: string;
  tool_id?: number;
  contact_info?: string;
  benefit_status: 'INACTIVE' | 'ACTIVE' | 'EXPIRED' | 'REVOKED';
  benefit_activated_at?: string;
  benefit_expires_at?: string;
  benefit_note?: string;
  admin_note?: string;
  paid_at?: string;
  created_at: string;
  updated_at: string;
  feedback?: any;
  tool?: any;
  user?: any;
}

interface OrderStats {
  total: number;
  pending: number;
  paid: number;
  activeBenefits: number;
  totalRevenue: number;
  byPlan: Record<string, number>;
}

const AdminOrderPage: React.FC = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });

  // 筛选
  const [paymentFilter, setPaymentFilter] = useState<string>('');
  const [benefitFilter, setBenefitFilter] = useState<string>('');
  const [planFilter, setPlanFilter] = useState<string>('');
  const [keyword, setKeyword] = useState('');

  // 弹窗
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [benefitModalVisible, setBenefitModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);

  // 批量开通权益
  const [batchPlanFilter, setBatchPlanFilter] = useState<string>('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResultVisible, setBatchResultVisible] = useState(false);
  const [batchResult, setBatchResult] = useState<any>(null);

  const [paymentForm] = Form.useForm();
  const [benefitForm] = Form.useForm();
  const [createForm] = Form.useForm();

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: pagination.page, pageSize: pagination.pageSize };
      if (paymentFilter) params.payment_status = paymentFilter;
      if (benefitFilter) params.benefit_status = benefitFilter;
      if (planFilter) params.plan_type = planFilter;
      if (keyword) params.keyword = keyword;

      const res = await orderAPI.getList(params);
      setOrders(res.data.orders || []);
      setPagination(prev => ({ ...prev, total: res.data.pagination?.total || 0 }));
    } catch (error) {
      console.error('获取订单列表失败:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, paymentFilter, benefitFilter, planFilter, keyword]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await orderAPI.getStats();
      setStats(res.data);
    } catch (error) {
      console.error('获取统计失败:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ==================== 支付状态操作 ====================
  const handlePaymentUpdate = async (values: any) => {
    if (!currentOrder) return;
    try {
      await orderAPI.updatePayment(currentOrder.id, values);
      message.success(t('orders.paymentUpdated'));
      setPaymentModalVisible(false);
      paymentForm.resetFields();
      fetchOrders();
      fetchStats();
    } catch (error) {
      message.error(t('orders.updateFailed'));
    }
  };

  // ==================== 权益操作 ====================
  const handleBenefitUpdate = async (values: any) => {
    if (!currentOrder) return;
    try {
      await orderAPI.updateBenefit(currentOrder.id, values);
      message.success(t('orders.benefitUpdated'));
      setBenefitModalVisible(false);
      benefitForm.resetFields();
      fetchOrders();
      fetchStats();
    } catch (error) {
      message.error(t('orders.updateFailed'));
    }
  };

  // ==================== 批量开通权益 ====================
  const handleBatchGrant = async (dryRun = false) => {
    try {
      setBatchLoading(true);
      const res = await orderAPI.batchGrant({
        ...(batchPlanFilter ? { plan_type: batchPlanFilter } : {}),
        dry_run: dryRun,
      });
      setBatchResult(res.data);
      setBatchResultVisible(true);
      if (!dryRun && res.data.success > 0) {
        message.success(`成功开通 ${res.data.success} 条权益！`);
        fetchOrders();
        fetchStats();
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || '批量操作失败，请重试');
    } finally {
      setBatchLoading(false);
    }
  };

  // ==================== 创建订单 ====================
  const handleCreateOrder = async (values: any) => {
    try {
      await orderAPI.create(values);
      message.success(t('orders.createSuccess'));
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchOrders();
      fetchStats();
    } catch (error) {
      message.error(t('orders.createFailed'));
    }
  };

  // ==================== 渲染辅助 ====================
  const planColors: Record<string, string> = { free: 'green', standard: 'blue', premium: 'gold' };
  const planLabels: Record<string, string> = {
    free: t('orders.planFree'),
    standard: t('orders.planStd'),
    premium: t('orders.planPro'),
  };
  const planIcons: Record<string, React.ReactNode> = {
    free: <CheckCircleOutlined />,
    standard: <StarOutlined />,
    premium: <CrownOutlined />,
  };

  const paymentStatusColors: Record<string, string> = {
    PENDING: 'orange', PAID: 'green', EXPIRED: 'default', REFUNDED: 'red',
  };
  const paymentStatusLabels: Record<string, string> = {
    PENDING: t('orders.statusPending'),
    PAID: t('orders.statusPaid'),
    EXPIRED: t('orders.statusExpired'),
    REFUNDED: t('orders.statusRefunded'),
  };

  const benefitStatusColors: Record<string, string> = {
    INACTIVE: 'default', ACTIVE: 'green', EXPIRED: 'orange', REVOKED: 'red',
  };
  const benefitStatusLabels: Record<string, string> = {
    INACTIVE: t('orders.benefitInactive'),
    ACTIVE: t('orders.benefitActive'),
    EXPIRED: t('orders.benefitExpired'),
    REVOKED: t('orders.benefitRevoked'),
  };

  const columns = [
    {
      title: t('orders.orderNo'),
      dataIndex: 'order_no',
      key: 'order_no',
      width: 180,
      render: (v: string) => <Text copyable style={{ fontSize: 12, fontFamily: 'monospace' }}>{v}</Text>,
    },
    {
      title: t('orders.toolName'),
      dataIndex: 'tool_name',
      key: 'tool_name',
      width: 180,
      ellipsis: true,
      render: (v: string, r: Order) => (
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          {r.user && <Text type="secondary" style={{ fontSize: 12 }}>{r.user.username || r.user.email}</Text>}
        </div>
      ),
    },
    {
      title: t('orders.plan'),
      dataIndex: 'plan_type',
      key: 'plan_type',
      width: 120,
      render: (v: string) => (
        <Tag color={planColors[v]} icon={planIcons[v]}>
          {planLabels[v] || v}
        </Tag>
      ),
    },
    {
      title: t('orders.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (v: number, r: Order) => {
        const symbol = r.currency === 'USD' ? '$' : '¥';
        return <Text strong style={{ color: v > 0 ? '#f5222d' : '#52c41a' }}>{symbol}{Number(v).toFixed(2)}</Text>;
      },
    },
    {
      title: t('orders.paymentStatus'),
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 110,
      render: (v: string) => <Tag color={paymentStatusColors[v]}>{paymentStatusLabels[v] || v}</Tag>,
    },
    {
      title: t('orders.benefitStatus'),
      dataIndex: 'benefit_status',
      key: 'benefit_status',
      width: 120,
      render: (v: string, r: Order) => {
        const isExpiring = r.benefit_expires_at && dayjs(r.benefit_expires_at).diff(dayjs(), 'day') < 7 && v === 'ACTIVE';
        return (
          <Tooltip title={r.benefit_expires_at ? `${t('orders.expiresAt')}: ${dayjs(r.benefit_expires_at).format('YYYY-MM-DD')}` : ''}>
            <Badge dot={isExpiring ? true : false} color="orange">
              <Tag color={benefitStatusColors[v]}>{benefitStatusLabels[v] || v}</Tag>
            </Badge>
          </Tooltip>
        );
      },
    },
    {
      title: t('orders.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('orders.actions'),
      key: 'actions',
      width: 220,
      fixed: 'right' as const,
      render: (_: any, record: Order) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => { setCurrentOrder(record); setDetailVisible(true); }}
          >
            {t('orders.detail')}
          </Button>
          {record.payment_status === 'PENDING' && (
            <Button
              type="link"
              size="small"
              style={{ color: '#52c41a' }}
              icon={<CheckCircleOutlined />}
              onClick={() => {
                setCurrentOrder(record);
                paymentForm.setFieldsValue({ payment_status: 'PAID' });
                setPaymentModalVisible(true);
              }}
            >
              {t('orders.confirmPay')}
            </Button>
          )}
          {record.payment_status === 'PAID' && record.benefit_status !== 'ACTIVE' && (
            <Button
              type="link"
              size="small"
              style={{ color: '#faad14' }}
              icon={<GiftOutlined />}
              onClick={() => {
                setCurrentOrder(record);
                benefitForm.setFieldsValue({ benefit_status: 'ACTIVE' });
                setBenefitModalVisible(true);
              }}
            >
              {t('orders.grantBenefit')}
            </Button>
          )}
          {record.benefit_status === 'ACTIVE' && (
            <Button
              type="link"
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => {
                setCurrentOrder(record);
                benefitForm.setFieldsValue({ benefit_status: 'REVOKED' });
                setBenefitModalVisible(true);
              }}
            >
              {t('orders.revokeBenefit')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>{t('orders.title')}</Title>

      {/* ==================== 统计卡片 ==================== */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title={t('orders.totalOrders')}
              value={stats?.total || 0}
              prefix={<ShoppingOutlined style={{ color: '#1677ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title={t('orders.totalRevenue')}
              value={stats?.totalRevenue || 0}
              prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
              suffix="¥"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title={t('orders.pendingPayment')}
              value={stats?.pending || 0}
              prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title={t('orders.activeBenefits')}
              value={stats?.activeBenefits || 0}
              prefix={<GiftOutlined style={{ color: '#eb2f96' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* ==================== 批量开通权益 面板 ==================== */}
      <Card
        style={{
          marginBottom: 16,
          border: '1.5px solid #faad1440',
          background: 'linear-gradient(135deg, #fffbe6, #fff7e680)',
        }}
        bodyStyle={{ padding: '14px 20px' }}
      >
        <Row align="middle" gutter={[16, 8]}>
          <Col flex="none">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ThunderboltOutlined style={{ fontSize: 20, color: '#faad14' }} />
              <Text strong style={{ fontSize: 15, color: '#ad6800' }}>批量开通权益</Text>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              一键为所有「已付款 + 权益未激活」的订单开通权益
            </Text>
          </Col>
          <Col flex="none">
            <Select
              placeholder="全部套餐"
              value={batchPlanFilter || undefined}
              onChange={v => setBatchPlanFilter(v || '')}
              allowClear
              style={{ width: 130 }}
              size="middle"
            >
              <Option value="standard">标准收录</Option>
              <Option value="premium">首页曝光</Option>
            </Select>
          </Col>
          <Col flex="none">
            <Space>
              <Button
                icon={<EyeOutlined />}
                loading={batchLoading}
                onClick={() => handleBatchGrant(true)}
              >
                预览待开通
              </Button>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={batchLoading}
                style={{
                  background: 'linear-gradient(135deg, #faad14, #ff6b00)',
                  border: 'none',
                  fontWeight: 600,
                }}
                onClick={() => {
                  Modal.confirm({
                    title: '确认批量开通权益',
                    content: `将为所有「已付款且权益未激活」${batchPlanFilter ? `（${batchPlanFilter}）` : ''}的订单自动开通权益，该操作不可撤销，确认继续？`,
                    okText: '确认开通',
                    cancelText: '取消',
                    okButtonProps: { danger: false, style: { background: '#faad14', border: 'none' } },
                    onOk: () => handleBatchGrant(false),
                  });
                }}
              >
                一键批量开通
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ==================== 筛选工具栏 ==================== */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={6}>
            <Input
              placeholder={t('orders.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onPressEnter={() => fetchOrders()}
              allowClear
            />
          </Col>
          <Col xs={12} sm={4}>
            <Select
              placeholder={t('orders.paymentStatus')}
              value={paymentFilter || undefined}
              onChange={v => { setPaymentFilter(v || ''); setPagination(p => ({ ...p, page: 1 })); }}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="PENDING">{t('orders.statusPending')}</Option>
              <Option value="PAID">{t('orders.statusPaid')}</Option>
              <Option value="EXPIRED">{t('orders.statusExpired')}</Option>
              <Option value="REFUNDED">{t('orders.statusRefunded')}</Option>
            </Select>
          </Col>
          <Col xs={12} sm={4}>
            <Select
              placeholder={t('orders.benefitStatus')}
              value={benefitFilter || undefined}
              onChange={v => { setBenefitFilter(v || ''); setPagination(p => ({ ...p, page: 1 })); }}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="INACTIVE">{t('orders.benefitInactive')}</Option>
              <Option value="ACTIVE">{t('orders.benefitActive')}</Option>
              <Option value="EXPIRED">{t('orders.benefitExpired')}</Option>
              <Option value="REVOKED">{t('orders.benefitRevoked')}</Option>
            </Select>
          </Col>
          <Col xs={12} sm={4}>
            <Select
              placeholder={t('orders.plan')}
              value={planFilter || undefined}
              onChange={v => { setPlanFilter(v || ''); setPagination(p => ({ ...p, page: 1 })); }}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="free">{t('orders.planFree')}</Option>
              <Option value="standard">{t('orders.planStd')}</Option>
              <Option value="premium">{t('orders.planPro')}</Option>
            </Select>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => { fetchOrders(); fetchStats(); }}>
                {t('orders.refresh')}
              </Button>
              <Button type="primary" onClick={() => setCreateModalVisible(true)}>
                + {t('orders.createOrder')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ==================== 订单列表 ==================== */}
      <Card>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `${t('orders.total')} ${total} ${t('orders.records')}`,
            onChange: (page, pageSize) => setPagination(prev => ({ ...prev, page, pageSize })),
          }}
        />
      </Card>

      {/* ==================== 订单详情弹窗 ==================== */}
      <Modal
        title={t('orders.orderDetail')}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={680}
      >
        {currentOrder && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label={t('orders.orderNo')}>{currentOrder.order_no}</Descriptions.Item>
              <Descriptions.Item label={t('orders.createdAt')}>
                {dayjs(currentOrder.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.toolName')}>{currentOrder.tool_name}</Descriptions.Item>
              <Descriptions.Item label={t('orders.toolUrl')}>
                {currentOrder.tool_url ? (
                  <a href={currentOrder.tool_url} target="_blank" rel="noreferrer">{currentOrder.tool_url}</a>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.plan')}>
                <Tag color={planColors[currentOrder.plan_type]} icon={planIcons[currentOrder.plan_type]}>
                  {planLabels[currentOrder.plan_type]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.amount')}>
                <Text strong style={{ color: '#f5222d', fontSize: 16 }}>¥{Number(currentOrder.amount).toFixed(2)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.paymentStatus')}>
                <Tag color={paymentStatusColors[currentOrder.payment_status]}>
                  {paymentStatusLabels[currentOrder.payment_status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.paymentMethod')}>
                {currentOrder.payment_method === 'wechat' ? t('orders.methodWechat') :
                  currentOrder.payment_method === 'alipay' ? t('orders.methodAlipay') :
                    currentOrder.payment_method || '-'}
              </Descriptions.Item>
              {currentOrder.paid_at && (
                <Descriptions.Item label={t('orders.paidAt')}>
                  {dayjs(currentOrder.paid_at).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('orders.contactInfo')}>{currentOrder.contact_info || '-'}</Descriptions.Item>
              {currentOrder.user && (
                <Descriptions.Item label={t('orders.user')}>
                  {currentOrder.user.username} ({currentOrder.user.email || currentOrder.user.phone})
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider>{t('orders.benefitInfo')}</Divider>

            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label={t('orders.benefitStatus')}>
                <Tag color={benefitStatusColors[currentOrder.benefit_status]}>
                  {benefitStatusLabels[currentOrder.benefit_status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.benefitActivatedAt')}>
                {currentOrder.benefit_activated_at
                  ? dayjs(currentOrder.benefit_activated_at).format('YYYY-MM-DD')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.benefitExpiresAt')}>
                {currentOrder.benefit_expires_at
                  ? dayjs(currentOrder.benefit_expires_at).format('YYYY-MM-DD')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.benefitNote')}>
                {currentOrder.benefit_note || '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* 权益说明 */}
            <Divider>{t('orders.benefitDescription')}</Divider>
            <div style={{ padding: 16, background: '#f9f9f9', borderRadius: 8 }}>
              {currentOrder.plan_type === 'free' && (
                <div>
                  <Tag color="green" icon={<CheckCircleOutlined />}>{t('orders.planFree')}</Tag>
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    <li>{t('orders.benefitFree1')}</li>
                    <li>{t('orders.benefitFree2')}</li>
                  </ul>
                </div>
              )}
              {currentOrder.plan_type === 'standard' && (
                <div>
                  <Tag color="blue" icon={<StarOutlined />}>{t('orders.planStd')}</Tag>
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    <li>{t('orders.benefitStd1')}</li>
                    <li>{t('orders.benefitStd2')}</li>
                    <li>{t('orders.benefitStd3')}</li>
                    <li>{t('orders.benefitStd4')}</li>
                  </ul>
                </div>
              )}
              {currentOrder.plan_type === 'premium' && (
                <div>
                  <Tag color="gold" icon={<CrownOutlined />}>{t('orders.planPro')}</Tag>
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    <li>{t('orders.benefitPro1')}</li>
                    <li>{t('orders.benefitPro2')}</li>
                    <li>{t('orders.benefitPro3')}</li>
                    <li>{t('orders.benefitPro4')}</li>
                    <li>{t('orders.benefitPro5')}</li>
                  </ul>
                </div>
              )}
            </div>

            {currentOrder.admin_note && (
              <>
                <Divider>{t('orders.adminNote')}</Divider>
                <Text>{currentOrder.admin_note}</Text>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ==================== 确认支付弹窗 ==================== */}
      <Modal
        title={t('orders.confirmPayTitle')}
        open={paymentModalVisible}
        onCancel={() => { setPaymentModalVisible(false); paymentForm.resetFields(); }}
        onOk={() => paymentForm.submit()}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
      >
        <Form form={paymentForm} layout="vertical" onFinish={handlePaymentUpdate}>
          <Form.Item name="payment_status" label={t('orders.paymentStatus')} rules={[{ required: true }]}>
            <Select>
              <Option value="PAID">{t('orders.statusPaid')}</Option>
              <Option value="EXPIRED">{t('orders.statusExpired')}</Option>
              <Option value="REFUNDED">{t('orders.statusRefunded')}</Option>
            </Select>
          </Form.Item>
          <Form.Item name="payment_method" label={t('orders.paymentMethod')}>
            <Select allowClear>
              <Option value="wechat">{t('orders.methodWechat')}</Option>
              <Option value="alipay">{t('orders.methodAlipay')}</Option>
              <Option value="other">{t('orders.methodOther')}</Option>
            </Select>
          </Form.Item>
          <Form.Item name="admin_note" label={t('orders.adminNote')}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ==================== 权益管理弹窗 ==================== */}
      <Modal
        title={t('orders.manageBenefit')}
        open={benefitModalVisible}
        onCancel={() => { setBenefitModalVisible(false); benefitForm.resetFields(); }}
        onOk={() => benefitForm.submit()}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
      >
        <Form form={benefitForm} layout="vertical" onFinish={handleBenefitUpdate}>
          <Form.Item name="benefit_status" label={t('orders.benefitStatus')} rules={[{ required: true }]}>
            <Select>
              <Option value="ACTIVE">{t('orders.benefitActive')}</Option>
              <Option value="EXPIRED">{t('orders.benefitExpired')}</Option>
              <Option value="REVOKED">{t('orders.benefitRevoked')}</Option>
            </Select>
          </Form.Item>
          <Form.Item name="expires_days" label={t('orders.expiresDays')}>
            <Select allowClear placeholder={t('orders.expiresDaysHint')}>
              <Option value={30}>30 {t('orders.days')}</Option>
              <Option value={90}>90 {t('orders.days')}</Option>
              <Option value={180}>180 {t('orders.days')}</Option>
              <Option value={365}>365 {t('orders.days')}</Option>
            </Select>
          </Form.Item>
          <Form.Item name="benefit_note" label={t('orders.benefitNote')}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
        {currentOrder && (
          <div style={{ padding: 12, background: '#fffbe6', borderRadius: 8, marginTop: 8 }}>
            <Text type="warning" strong>{t('orders.benefitHint')}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {currentOrder.plan_type === 'standard' && t('orders.benefitHintStd')}
              {currentOrder.plan_type === 'premium' && t('orders.benefitHintPro')}
            </Text>
          </div>
        )}
      </Modal>

      {/* ==================== 创建订单弹窗 ==================== */}
      <Modal
        title={t('orders.createOrder')}
        open={createModalVisible}
        onCancel={() => { setCreateModalVisible(false); createForm.resetFields(); }}
        onOk={() => createForm.submit()}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        width={560}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateOrder}>
          <Form.Item name="tool_name" label={t('orders.toolName')} rules={[{ required: true, message: t('orders.toolNameRequired') }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tool_url" label={t('orders.toolUrl')}>
            <Input />
          </Form.Item>
          <Form.Item name="plan_type" label={t('orders.plan')} rules={[{ required: true }]}>
            <Select>
              <Option value="free">{t('orders.planFree')} (¥0)</Option>
              <Option value="standard">{t('orders.planStd')} (¥50)</Option>
              <Option value="premium">{t('orders.planPro')} (¥698)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="contact_info" label={t('orders.contactInfo')}>
            <Input />
          </Form.Item>
          <Form.Item name="payment_method" label={t('orders.paymentMethod')}>
            <Select allowClear>
              <Option value="wechat">{t('orders.methodWechat')}</Option>
              <Option value="alipay">{t('orders.methodAlipay')}</Option>
              <Option value="other">{t('orders.methodOther')}</Option>
            </Select>
          </Form.Item>
          <Form.Item name="admin_note" label={t('orders.adminNote')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ==================== 批量开通结果弹窗 ==================== */}
      <Modal
        title={
          <Space>
            <ThunderboltOutlined style={{ color: '#faad14' }} />
            {batchResult?.preview ? '待开通权益预览' : '批量开通权益结果'}
          </Space>
        }
        open={batchResultVisible}
        onCancel={() => setBatchResultVisible(false)}
        footer={
          <Space>
            <Button onClick={() => setBatchResultVisible(false)}>关闭</Button>
            {batchResult?.preview && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                style={{ background: '#faad14', border: 'none' }}
                onClick={() => {
                  setBatchResultVisible(false);
                  Modal.confirm({
                    title: `确认开通 ${batchResult.total} 条权益？`,
                    okText: '确认开通',
                    onOk: () => handleBatchGrant(false),
                  });
                }}
              >
                确认开通 {batchResult?.total} 条
              </Button>
            )}
          </Space>
        }
        width={680}
      >
        {batchResult && (
          <div>
            {/* 汇总信息 */}
            <Alert
              type={batchResult.preview ? 'info' : batchResult.failed > 0 ? 'warning' : 'success'}
              showIcon
              message={batchResult.message}
              style={{ marginBottom: 16 }}
            />
            {!batchResult.preview && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                  <Card size="small" style={{ textAlign: 'center', borderColor: '#d9d9d9' }}>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>{batchResult.total}</div>
                    <div style={{ color: '#888', fontSize: 12 }}>总计</div>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small" style={{ textAlign: 'center', borderColor: '#52c41a' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{batchResult.success}</div>
                    <div style={{ color: '#52c41a', fontSize: 12 }}>成功</div>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small" style={{ textAlign: 'center', borderColor: batchResult.failed > 0 ? '#ff4d4f' : '#d9d9d9' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: batchResult.failed > 0 ? '#ff4d4f' : '#999' }}>{batchResult.failed}</div>
                    <div style={{ color: batchResult.failed > 0 ? '#ff4d4f' : '#999', fontSize: 12 }}>失败</div>
                  </Card>
                </Col>
              </Row>
            )}
            {/* 详细日志 */}
            {batchResult.logs?.length > 0 && (
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                <List
                  size="small"
                  bordered
                  dataSource={batchResult.logs}
                  renderItem={(item: any) => (
                    <List.Item
                      extra={
                        item.status === 'success' ? (
                          <Tag color="green" icon={<CheckCircleFilled />}>已开通</Tag>
                        ) : item.status === 'failed' ? (
                          <Tag color="red" icon={<CloseCircleFilled />}>失败</Tag>
                        ) : (
                          <Tag color="orange">待开通</Tag>
                        )
                      }
                    >
                      <List.Item.Meta
                        title={
                          <Space size={6}>
                            <Text strong>{item.tool_name}</Text>
                            <Tag color={item.plan_type === 'premium' ? 'gold' : 'blue'} style={{ fontSize: 11 }}>
                              {item.plan_type}
                            </Tag>
                          </Space>
                        }
                        description={
                          <Space size={16} style={{ fontSize: 12, color: '#888' }}>
                            <span>#{item.order_no}</span>
                            {item.contact_info && <span>📬 {item.contact_info}</span>}
                            {item.message && item.status === 'failed' && (
                              <Text type="danger" style={{ fontSize: 12 }}>{item.message}</Text>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminOrderPage;
